import React, { useContext, useEffect } from 'react';
import classes from './../BulkImportContent/BulkImportContent.module.scss';
import { UseFormReturn } from 'react-hook-form';
import { CheckboxWithAction } from '../../../components/actions/CheckboxWithAction';
import { BulkActionsContext } from '../states/BulkActionsProvider';
import { PostStatusSelector } from '../PostStatusSelector';
import { PostFeedType, PostStatus, PostPublishMethod } from '../../../models/graphql';
import { FormElementAction } from '../../../models/basic-objects';
import { SocialProfileSelector } from '../../../components/socialProfiles/SocialProfileSelector';
import { Select } from '../../../components/forms/Select';
import { getCategorySelectTemplates, MappedCategoriesType } from '../../Publish/Categories/Categories.helpers';
import { SelectItem } from '../../../components/forms/Select/Select.view';
import classNames from 'classnames';
import { BoxNotification } from '../../../components/containers/BoxNotification';

export type BulkImportTargetingViewProps = {
  //
  formInstance: UseFormReturn;
};

export const BulkImportTargetingView = (props: BulkImportTargetingViewProps): JSX.Element => {
  const { formInstance } = props;
  const { state, dispatch, profiles, categories } = useContext(BulkActionsContext);
  const [valueTemplate, itemTemplate] = getCategorySelectTemplates(categories);
  const mappedCategories: SelectItem[] = categories.map((category: MappedCategoriesType) => ({
    label: category.name,
    value: category.id
  }));
  const { targeting } = state;
  const { checkboxes, content } = targeting;
  const {
    postStatus: postStatusValue,
    profiles: selectedProfiles,
    category: selectedCategoryId,
    instagramPostFeedType,
    instagramPublishMethod
  } = content;

  const checkboxesState = Object.values(checkboxes).reduce((prev, current) => {
    if (prev) return prev;
    return current;
  });

  const hasIgProfile = !!profiles.find(p => p.__typename === 'InstagramSocialProfile');

  useEffect(() => {
    dispatch({ type: 'setChanges', payload: { section: 'targeting', value: checkboxesState } });
  }, [checkboxesState, dispatch]);

  useEffect(() => {
    if (!hasIgProfile) {
      dispatch({ type: 'resetInstagramPublishing' });
    }
  }, [hasIgProfile, dispatch]);

  const updatePostStatus: FormElementAction<PostStatus> = (value: PostStatus, complete) => {
    complete();
    dispatch({ type: 'setPostStatus', payload: value });
  };

  const updatePostFeedType: FormElementAction = (value, complete) => {
    complete();
    dispatch({ type: 'setInstagramPostFeedType', payload: value as PostFeedType });
  };

  const updatePublishingMethod: FormElementAction = (value, complete) => {
    complete();
    dispatch({ type: 'setInstagramPublishingMethod', payload: value as PostPublishMethod });
  };

  const {
    postStatus: postStatusCheckbox,
    profiles: profilesCheckbox,
    category: categoryCheckbox,
    instagramPostFeedType: instagramPostFeedTypeCheckbox,
    instagramPublishMethod: instagramPublishMethodCheckbox
  } = checkboxes;

  return (
    <>
      {/* Post Status */}
      <div className={'grid-x'}>
        <div className={'cell small-12'}>
          <CheckboxWithAction
            name={'postStatusCheckbox'}
            size={'small'}
            onClickHandler={() => {
              dispatch({ type: 'toggleCheckbox', payload: { section: 'targeting', key: 'postStatus' } });
            }}
            wrapper={true}
            checked={postStatusCheckbox}
            label={'POST STATUS'}
          />
        </div>
        <div className={classNames(classes.InnerBlock, classes.InnerBlockAutoWidth)}>
          <div className={'cell shrink'}>
            <PostStatusSelector
              disabled={!postStatusCheckbox}
              callback={updatePostStatus}
              formInstance={formInstance}
              customizeType={'global'}
              readonly={!postStatusCheckbox}
              {...(postStatusValue ? { value: postStatusValue } : {})}
            />
          </div>
        </div>
      </div>
      {/* Profile Selector */}
      <div className={'grid-x'}>
        <div className={'cell small-12'}>
          <CheckboxWithAction
            name={'profilesCheckbox'}
            size={'small'}
            onClickHandler={() => {
              dispatch({ type: 'toggleCheckbox', payload: { section: 'targeting', key: 'profiles' } });
            }}
            wrapper={true}
            checked={profilesCheckbox}
            label={'SELECT PROFILE'}
          />
        </div>
        <div className={classes.InnerBlock}>
          <div className={'cell shrink'}>
            <SocialProfileSelector
              {...(selectedProfiles ? { selectedProfiles } : {})}
              disabledProfileIds={[]}
              disabled={!profilesCheckbox}
              usePinterestBoard={true}
              useTikTokSettings={true}
              groupByProfile={false}
              selectItems={profiles}
              useFormInstance={formInstance}
              formFieldName={'selectProfile'}
              selectAllFormName={'selectAllProfiles'}
            />
          </div>
        </div>
      </div>
      {/* Category Selector */}
      <div className={'grid-x'}>
        <div className={'cell small-12'}>
          <CheckboxWithAction
            name={'categoryCheckbox'}
            size={'small'}
            onClickHandler={() => {
              dispatch({ type: 'toggleCheckbox', payload: { section: 'targeting', key: 'category' } });
            }}
            wrapper={true}
            checked={categoryCheckbox}
            label={'Select a Category'}
          />
        </div>
        <div className={classes.InnerBlock}>
          <div className={'cell shrink'}>
            <Select
              disabled={!categoryCheckbox}
              chooseLabel={'Select a Category'}
              hideDefaultSelectOption={true}
              items={mappedCategories}
              name={'selectCategory'}
              size={'large'}
              rules={{}}
              valueTemplate={valueTemplate}
              itemTemplate={itemTemplate}
              useFormInstance={formInstance}
              {...(selectedCategoryId ? { selectedValue: selectedCategoryId } : {})}
            />
          </div>
        </div>
      </div>
      {/* Instagram publishing sections*/}
      {hasIgProfile ? (
        <>
          <div className={'grid-x'}>
            <div className={'cell small-12'}>
              <CheckboxWithAction
                name={'instagramPostFeedTypeCheckbox'}
                size={'small'}
                onClickHandler={() => {
                  dispatch({ type: 'toggleCheckbox', payload: { section: 'targeting', key: 'instagramPostFeedType' } });
                }}
                wrapper={true}
                checked={instagramPostFeedTypeCheckbox}
                label={'INSTAGRAM POST TYPE'}
              />
            </div>
            <div className={classes.InnerBlock}>
              <div className={'cell shrink'}>
                <Select
                  action={updatePostFeedType}
                  disabled={!instagramPostFeedTypeCheckbox}
                  items={[
                    { label: 'Feed/Reel', value: PostFeedType.FEED },
                    { label: 'Story', value: PostFeedType.STORY }
                  ]}
                  name={'instagramPostFeedType'}
                  size={'large'}
                  rules={{}}
                  useFormInstance={formInstance}
                  selectedValue={instagramPostFeedType}
                />
              </div>
            </div>
          </div>
          <div className={'grid-x'}>
            <div className={'cell small-12'}>
              <CheckboxWithAction
                name={'instagramPublishMethodCheckbox'}
                size={'small'}
                onClickHandler={() => {
                  dispatch({
                    type: 'toggleCheckbox',
                    payload: { section: 'targeting', key: 'instagramPublishMethod' }
                  });
                }}
                wrapper={true}
                checked={instagramPublishMethodCheckbox}
                label={'INSTAGRAM PUBLISHING'}
              />
            </div>
            <div className={classes.InnerBlock}>
              <div className={'cell shrink'}>
                <Select
                  wrapper={true}
                  action={updatePublishingMethod}
                  disabled={!instagramPublishMethodCheckbox}
                  hideDefaultSelectOption={true}
                  items={[
                    { label: 'Direct Publishing', value: PostPublishMethod.DIRECT },
                    { label: 'Receive a Notification', value: PostPublishMethod.MANUAL }
                  ]}
                  name={'instagramPublishMethod'}
                  size={'large'}
                  rules={{}}
                  useFormInstance={formInstance}
                  {...(instagramPublishMethod ? { selectedValue: instagramPublishMethod } : {})}
                />
              </div>
              <div className={'cell small-12'}>
                <BoxNotification type={'info'}>
                  Publishing method depends on the profile type. Make sure to have direct publishing enabled for
                  eligible profiles, in all other cases we’ll notify you via mobile or email notifications when your
                  post is due. To receive mobile reminders, consider installing our mobile app.
                </BoxNotification>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
};
